import { useState } from 'react';
import { useProjects } from '../hooks/useProjects';
import { useApi } from '../hooks/useApi';
import { useToast } from '../components/ui/Toast';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import type { CreateProjectInput, UpdateProjectInput, ProjectRepo, Project, UnifiedJiraSpace } from '@pantheon/shared';

/** Components of the unified PRESTO space — each ticket carries exactly one. */
const UNIFIED_COMPONENTS = [
  'Stats', 'Statview', 'Record Book', 'CMS Legacy', 'Gameday 2.0', 'Website 2.0', 'Camps 2.0', 'Awards 2.0',
  'User Management', 'Client Management', 'PrestoAI', 'PrestoTools', 'Pantheon', 'FED', 'Infrastructure', 'Mobile',
];
const CMS_LEGACY_LABELS = ['website', 'gameday', 'camps', 'awards'];

const emptyRepo: ProjectRepo = {
  label: '', repoUrl: '', repoProvider: 'bitbucket', defaultBranch: 'main',
  docsPaths: ['docs/', 'README.md'], codePaths: ['src/'],
};

const emptyForm: CreateProjectInput = {
  name: '', repos: [{ ...emptyRepo }],
  jira: { projectKey: '', baseUrl: '', daedalusLabel: '', defaultIssueType: 'Task' },
};

export function ProjectsPage() {
  const { projects, loading, error, refetch } = useProjects();
  const { post, put, del } = useApi();
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<CreateProjectInput>({ ...emptyForm, repos: [{ ...emptyRepo }] });

  const isEditing = editingId !== null;

  const openCreate = () => { setEditingId(null); setForm({ ...emptyForm, repos: [{ ...emptyRepo }] }); setShowForm(true); };
  const openEdit = (p: Project) => { setEditingId(p.projectId); setForm({ name: p.name, notes: p.notes, repos: p.repos, googleDrive: p.googleDrive, jira: p.jira, settings: p.settings }); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditingId(null); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const result = isEditing
      ? await put<Project>(`/projects/${editingId}`, form as UpdateProjectInput)
      : await post<Project>('/projects', form);
    if (result.success) { closeForm(); refetch(); toast(isEditing ? 'Project updated' : 'Project created', 'success'); }
    else { toast(result.error?.message ?? 'Failed to save', 'error'); }
    setSaving(false);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete project "${name}"?`)) return;
    await del(`/projects/${id}`);
    refetch();
    toast('Project deleted', 'info');
  };

  const setUnified = (patch: Partial<UnifiedJiraSpace>) => {
    const current: UnifiedJiraSpace = form.jira.unifiedSpace ?? { projectKey: 'PRESTO', component: '' };
    const next = { ...current, ...patch };
    if (next.component !== 'CMS Legacy') delete next.productLabel;
    setForm({ ...form, jira: { ...form.jira, unifiedSpace: next } });
  };

  const updateRepo = (i: number, field: keyof ProjectRepo, value: unknown) => {
    const repos = [...form.repos];
    repos[i] = { ...repos[i], [field]: value };
    setForm({ ...form, repos });
  };

  const input = 'w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary';
  const label = 'block text-sm font-semibold text-gray-700 mb-1';

  return (
    <div className="animate-rise">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center text-base font-bold shrink-0 bg-primary-light text-primary">
            P
          </span>
          <h1 className="text-2xl font-bold">Projects</h1>
        </div>
        <Button onClick={showForm ? closeForm : openCreate} variant={showForm ? 'secondary' : 'primary'} size="sm">
          {showForm ? 'Cancel' : 'Add Project'}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-100 shadow-[0_1px_2px_rgba(15,23,42,.04),0_10px_30px_-14px_rgba(15,23,42,.18)] p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">{isEditing ? 'Edit Project' : 'New Project'}</h3>

          <label className={label}>Project Name</label>
          <input className={`${input} mb-4`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Statview" required />

          <label className={label}>
            Important Notes
            <span className="text-xs text-gray-500 font-normal ml-2">Instructions for AI agents when working on this project</span>
          </label>
          <textarea
            className={`${input} resize-y mb-4 font-mono text-xs`}
            rows={4}
            value={(form as any).notes ?? ''}
            onChange={(e) => setForm({ ...form, notes: e.target.value || undefined } as any)}
            placeholder="e.g., This project uses a custom ORM — never use raw SQL. All API routes must go through the middleware in src/middleware/auth.ts..."
          />

          {/* Repos */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">Repositories</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => setForm({ ...form, repos: [...form.repos, { ...emptyRepo }] })}>+ Add Repo</Button>
            </div>
            {form.repos.map((repo, i) => (
              <div key={i} className="border border-gray-100 rounded-xl p-4 mb-2 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-500">Repo {i + 1}</span>
                  {form.repos.length > 1 && (
                    <button type="button" onClick={() => setForm({ ...form, repos: form.repos.filter((_, j) => j !== i) })} className="text-xs text-danger hover:underline">Remove</button>
                  )}
                </div>
                <label className={label}>Label</label>
                <input className={`${input} mb-2`} value={repo.label} onChange={(e) => updateRepo(i, 'label', e.target.value)} placeholder="UI, API, Core" required />
                <label className={label}>Bitbucket Repo URL</label>
                <input className={`${input} mb-2`} value={repo.repoUrl} onChange={(e) => updateRepo(i, 'repoUrl', e.target.value)} placeholder="https://bitbucket.org/workspace/repo" required />
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className={label}>Branch</label>
                    <input className={input} value={repo.defaultBranch} onChange={(e) => updateRepo(i, 'defaultBranch', e.target.value)} />
                  </div>
                  <div>
                    <label className={label}>Doc Paths</label>
                    <input className={input} value={repo.docsPaths.join(', ')} onChange={(e) => updateRepo(i, 'docsPaths', e.target.value.split(',').map(s => s.trim()))} />
                  </div>
                  <div>
                    <label className={label}>Code Paths</label>
                    <input className={input} value={repo.codePaths.join(', ')} onChange={(e) => updateRepo(i, 'codePaths', e.target.value.split(',').map(s => s.trim()))} />
                  </div>
                </div>
                <label className={`${label} mt-2`}>Verify Commands (comma-separated; run before the PR so CI passes, e.g. npm ci, npm run lint, npm test)</label>
                <input className={input} value={(repo.verifyCommands ?? []).join(', ')} onChange={(e) => updateRepo(i, 'verifyCommands', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} />
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <label className={label}>App Start Command (screenshots)</label>
                    <input className={input} value={repo.appStartCommand ?? ''} onChange={(e) => updateRepo(i, 'appStartCommand', e.target.value || undefined)} placeholder="npm run dev" />
                  </div>
                  <div>
                    <label className={label}>App URL</label>
                    <input className={input} value={repo.appUrl ?? ''} onChange={(e) => updateRepo(i, 'appUrl', e.target.value || undefined)} placeholder="http://localhost:3000" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Drive */}
          <p className="text-sm font-semibold text-gray-700 mb-2">Google Drive (optional)</p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className={label}>Folder URL or ID</label>
              <input className={input} value={form.googleDrive?.folderId ?? ''} onChange={(e) => {
                const raw = e.target.value.trim();
                if (!raw) { setForm({ ...form, googleDrive: undefined }); return; }
                const match = raw.match(/folders\/([a-zA-Z0-9_-]+)/);
                setForm({ ...form, googleDrive: { folderId: match ? match[1] : raw, folderName: form.googleDrive?.folderName } });
              }} placeholder="Paste Drive folder URL" />
            </div>
            <div>
              <label className={label}>Folder Label</label>
              <input className={input} value={form.googleDrive?.folderName ?? ''} onChange={(e) => {
                if (!form.googleDrive?.folderId) return;
                setForm({ ...form, googleDrive: { ...form.googleDrive, folderName: e.target.value } });
              }} placeholder="e.g., Statview Docs" />
            </div>
          </div>

          {/* Jira */}
          <p className="text-sm font-semibold text-gray-700 mb-2">Jira</p>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div>
              <label className={label}>Base URL</label>
              <input className={input} value={form.jira.baseUrl} onChange={(e) => setForm({ ...form, jira: { ...form.jira, baseUrl: e.target.value } })} placeholder="https://co.jira.com" required />
            </div>
            <div>
              <label className={label}>Project Key</label>
              <input className={input} value={form.jira.projectKey} onChange={(e) => setForm({ ...form, jira: { ...form.jira, projectKey: e.target.value } })} placeholder="SVIEW" required />
            </div>
            <div>
              <label className={label}>Agent</label>
              <select className={input} value={form.jira.daedalusLabel} onChange={(e) => setForm({ ...form, jira: { ...form.jira, daedalusLabel: e.target.value } })}>
                <option value="">None Selected</option>
                <option value="pantheon-daedalus">pantheon-daedalus</option>
              </select>
            </div>
          </div>

          {/* Unified Jira space */}
          <p className="text-sm font-semibold text-gray-700 mb-1">Unified Jira space</p>
          <p className="text-xs text-gray-500 mb-2">
            New Hermes plans create their tickets here, with this component and the <code>pantheon</code> label.
            Plans that already have tickets stay on the project key above. Leave the component blank to keep everything on the old key.
          </p>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div>
              <label className={label}>Space Key</label>
              <input className={input} value={form.jira.unifiedSpace?.projectKey ?? 'PRESTO'} onChange={(e) => setUnified({ projectKey: e.target.value })} placeholder="PRESTO" />
            </div>
            <div>
              <label className={label}>Component</label>
              <select className={input} value={form.jira.unifiedSpace?.component ?? ''} onChange={(e) => setUnified({ component: e.target.value })}>
                <option value="">Not moved (old key)</option>
                {UNIFIED_COMPONENTS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {form.jira.unifiedSpace?.component === 'CMS Legacy' && (
              <div>
                <label className={label}>Product Label</label>
                <select className={input} value={form.jira.unifiedSpace?.productLabel ?? ''} onChange={(e) => setUnified({ productLabel: e.target.value || undefined })} required>
                  <option value="">Choose one</option>
                  {CMS_LEGACY_LABELS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button type="submit" loading={saving}>{isEditing ? 'Save Changes' : 'Create Project'}</Button>
            <Button type="button" variant="secondary" onClick={closeForm}>Cancel</Button>
          </div>
        </form>
      )}

      {loading && <p className="text-gray-500 text-sm">Loading projects...</p>}
      {error && <p className="text-danger text-sm">{error}</p>}

      {!loading && projects.length === 0 && !showForm && (
        <EmptyState title="No projects configured yet" hint="Add a project to get started with Hermes." />
      )}

      <div className="space-y-3">
        {projects.map((project) => (
          <div key={project.projectId} className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-5 flex items-center justify-between">
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900">{project.name}</h3>
              {project.notes && (
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{project.notes}</p>
              )}
              <div className="flex flex-wrap gap-2 mt-1.5">
                <span className="text-xs text-gray-500 bg-gray-100 px-2.5 py-0.5 rounded-full">{project.jira.projectKey}</span>
                {project.jira.unifiedSpace && (
                  <span className="text-xs text-primary bg-primary/10 px-2.5 py-0.5 rounded-full" title="New plans create tickets here">
                    New: {project.jira.unifiedSpace.projectKey} · {project.jira.unifiedSpace.component}
                  </span>
                )}
                {project.repos.map((r) => (
                  <span key={r.repoUrl} className="text-xs text-gray-500 bg-gray-50 px-2.5 py-0.5 rounded-full border border-gray-100">
                    {r.label}
                  </span>
                ))}
                {project.googleDrive && (
                  <span className="text-xs text-success bg-success-light px-2.5 py-0.5 rounded-full">
                    Drive: {project.googleDrive.folderName ?? 'Connected'}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button variant="secondary" size="sm" onClick={() => openEdit(project)}>Edit</Button>
              <Button variant="danger" size="sm" onClick={() => handleDelete(project.projectId, project.name)}>Delete</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
